<script setup lang="ts">
import type {
	PropertyRecommendedShortsFeedSectionEmits,
	PropertyRecommendedShortsFeedSectionProps
} from '.';
import type { ShortVideo } from '~/composables/shorts';
import type { ShortsFilters } from '~/core/entities/property/filters';
import type { ComponentPublicInstance } from 'vue';

import {
	breakpointsTailwind,
	useBreakpoints,
	useIntersectionObserver
} from '@vueuse/core';
import { useFetchShorts } from '~/composables/shorts';
import { useOpenShortsFeed } from '~/composables/shorts/useOpenShortsFeed';
import { withSimilarRecommendationParams } from '~/composables/useShortsFilters/featuredVideosFilterRules';
import { useShortsListingInfiniteScroll } from '~/composables/useShortsListingInfiniteScroll';

import { Skeleton } from '~/components/ui/skeleton';
import { VideoCardHomeFeed } from '~/components/VideoCard';

const props = withDefaults(
	defineProps<PropertyRecommendedShortsFeedSectionProps>(),
	{
		sectionTitle: 'Imóveis selecionados para você',
		sectionSubtitle:
			'Explore shorts com características parecidas com este imóvel'
	}
);

const emit = defineEmits<PropertyRecommendedShortsFeedSectionEmits>();

const MOBILE_PAGE_SIZE = 4;
const DESKTOP_PAGE_SIZE = 10;
const API_PAGE_SIZE = 10;
const RECOMMENDATION_IMAGE_INTERVAL = 4;
const SKELETON_COUNT = 5;
const LOAD_MORE_ROOT_MARGIN = '400px 0px';

const { fetchVideos } = useFetchShorts();
const { openShortsFeed } = useOpenShortsFeed();
const isMobile = useBreakpoints(breakpointsTailwind).smaller('md');

const items = ref<ShortVideo[]>([]);
const queuedItems = ref<ShortVideo[]>([]);
const page = ref(1);
const apiPage = ref(1);
const apiHasMore = ref(false);
const hasMore = ref(false);
const isInitialLoading = ref(false);
const isLoadingMore = ref(false);
const hasLoadedOnce = ref(false);
const blockListingFetches = ref(false);
const loadMoreRef = ref<HTMLElement | null>(null);
const pageSize = computed(() =>
	isMobile.value ? MOBILE_PAGE_SIZE : DESKTOP_PAGE_SIZE
);
const requestId = ref(0);

function uniqueByCommercialId(videos: ShortVideo[]): ShortVideo[] {
	const seen = new Set<string>();
	return videos.filter(video => {
		const commercialId = video.property.commercialId;
		if (seen.has(commercialId)) return false;
		seen.add(commercialId);
		return true;
	});
}

function recommendationFilters(): ShortsFilters {
	return withSimilarRecommendationParams(
		{ includeImagesEvery: RECOMMENDATION_IMAGE_INTERVAL },
		props.referenceProperty
	);
}

function updateHasMore(): void {
	hasMore.value = queuedItems.value.length > 0 || apiHasMore.value;
}

function enqueueUnique(videos: ShortVideo[]): void {
	const combined = uniqueByCommercialId([
		...items.value,
		...queuedItems.value,
		...videos
	]);
	queuedItems.value = combined.slice(items.value.length);
}

async function loadInitial(): Promise<void> {
	const currentRequestId = ++requestId.value;
	items.value = [];
	queuedItems.value = [];
	page.value = 1;
	apiPage.value = 1;
	apiHasMore.value = false;
	hasMore.value = false;
	hasLoadedOnce.value = false;
	isLoadingMore.value = false;
	emit('update:has-recommendations', false);
	resetCardObserverState();
	resetLoadMoreCycle();

	if (!props.referenceProperty.trim()) {
		hasLoadedOnce.value = true;
		return;
	}

	isInitialLoading.value = true;
	try {
		const result = await fetchVideos(
			recommendationFilters(),
			1,
			API_PAGE_SIZE,
			{ enableRecommendationFallback: false }
		);

		if (currentRequestId !== requestId.value) return;

		const initialItems = uniqueByCommercialId(result.items);
		items.value = initialItems.slice(0, pageSize.value);
		queuedItems.value = initialItems.slice(pageSize.value);
		apiHasMore.value = result.hasMore;
		updateHasMore();
		hasLoadedOnce.value = true;
		emit('update:has-recommendations', items.value.length > 0);
	} finally {
		if (currentRequestId === requestId.value) {
			isInitialLoading.value = false;
		}
	}
}

async function loadMore(): Promise<boolean> {
	if (
		!hasMore.value ||
		isInitialLoading.value ||
		isLoadingMore.value ||
		blockListingFetches.value
	) {
		return false;
	}

	const nextPage = page.value + 1;
	const previousLength = items.value.length;
	const currentRequestId = requestId.value;
	isLoadingMore.value = true;

	try {
		if (queuedItems.value.length < pageSize.value && apiHasMore.value) {
			const nextApiPage = apiPage.value + 1;
			const result = await fetchVideos(
				recommendationFilters(),
				nextApiPage,
				API_PAGE_SIZE,
				{ enableRecommendationFallback: false }
			);

			if (currentRequestId !== requestId.value) return false;

			enqueueUnique(result.items);
			apiPage.value = nextApiPage;
			apiHasMore.value = result.hasMore;
		}

		const nextItems = queuedItems.value.splice(0, pageSize.value);
		items.value = [...items.value, ...nextItems];
		page.value = nextPage;
		updateHasMore();
		return items.value.length > previousLength;
	} finally {
		if (currentRequestId === requestId.value) {
			isLoadingMore.value = false;
		}
	}
}

const { resetLoadMoreCycle } = useShortsListingInfiniteScroll({
	loadMoreRef,
	hasMore,
	isLoading: isInitialLoading,
	isInfiniteLoading: isLoadingMore,
	blockListingFetches,
	onLoadMore: loadMore,
	rootMargin: LOAD_MORE_ROOT_MARGIN
});

type RecommendedCardTemplateRef = Element | ComponentPublicInstance;
type RecommendedCardObserverTarget =
	| HTMLElement
	| SVGElement
	| ComponentPublicInstance;

const observedCardRefs = shallowRef<RecommendedCardObserverTarget[]>([]);
const cardRefsByCommercialId = new Map<string, RecommendedCardObserverTarget>();
const cardRefCallbacks = new Map<
	string,
	(templateRef: RecommendedCardTemplateRef | null) => void
>();
const visibleRatios = new Map<string, number>();
const activeMobileCardCommercialId = ref<string | null>(null);
const videoCommercialIds = computed(
	() =>
		new Set(
			items.value
				.filter(item => Boolean(item.videoUrl))
				.map(item => item.property.commercialId)
		)
);

function updateActiveMobileCard(): void {
	if (!isMobile.value) {
		activeMobileCardCommercialId.value = null;
		return;
	}

	let nextCommercialId: string | null = null;
	let highestRatio = 0.55;

	for (const [commercialId, ratio] of visibleRatios) {
		if (!videoCommercialIds.value.has(commercialId) || ratio < highestRatio) {
			continue;
		}
		highestRatio = ratio;
		nextCommercialId = commercialId;
	}

	if (activeMobileCardCommercialId.value !== nextCommercialId) {
		activeMobileCardCommercialId.value = nextCommercialId;
	}
}

function setCardRef(
	commercialId: string,
	templateRef: RecommendedCardTemplateRef | null
): void {
	if (cardRefsByCommercialId.get(commercialId) === templateRef) return;

	if (templateRef) {
		cardRefsByCommercialId.set(
			commercialId,
			templateRef as RecommendedCardObserverTarget
		);
	} else {
		cardRefsByCommercialId.delete(commercialId);
		cardRefCallbacks.delete(commercialId);
		visibleRatios.delete(commercialId);
	}

	observedCardRefs.value = [...cardRefsByCommercialId.values()];
}

function getCardRef(
	commercialId: string
): (templateRef: RecommendedCardTemplateRef | null) => void {
	const currentCallback = cardRefCallbacks.get(commercialId);
	if (currentCallback) return currentCallback;

	const callback = (templateRef: RecommendedCardTemplateRef | null) =>
		setCardRef(commercialId, templateRef);
	cardRefCallbacks.set(commercialId, callback);

	return callback;
}

function resetCardObserverState(): void {
	observedCardRefs.value = [];
	cardRefsByCommercialId.clear();
	cardRefCallbacks.clear();
	visibleRatios.clear();
	activeMobileCardCommercialId.value = null;
}

const { stop: stopCardObserver } = useIntersectionObserver(
	observedCardRefs,
	entries => {
		for (const entry of entries) {
			const commercialId = (entry.target as HTMLElement).dataset
				.recommendedCardCommercialId;
			if (!commercialId) continue;

			if (!entry.isIntersecting) {
				visibleRatios.delete(commercialId);
				continue;
			}

			visibleRatios.set(commercialId, entry.intersectionRatio);
		}
		updateActiveMobileCard();
	},
	{
		rootMargin: '-10% 0px -10% 0px',
		threshold: [0, 0.25, 0.55, 0.75, 1]
	}
);

function handleCardClick(index: number): void {
	const item = items.value[index];
	if (!item) return;

	const filters = recommendationFilters();
	openShortsFeed({
		commercialId: item.property.commercialId,
		clickedIndex: index,
		entryContext: {
			items: [...items.value.slice(index + 1), ...queuedItems.value],
			filters,
			page: apiPage.value,
			perPage: API_PAGE_SIZE,
			hasMore: apiHasMore.value
		},
		queryFilters: filters
	});
}

watch(isMobile, updateActiveMobileCard);

watch(
	() => props.referenceProperty,
	(newReference, oldReference) => {
		if (!import.meta.client || newReference === oldReference) return;
		void loadInitial();
	}
);

onMounted(() => {
	void loadInitial();
});

onBeforeUnmount(() => {
	blockListingFetches.value = true;
	requestId.value += 1;
	stopCardObserver();
	resetCardObserverState();
});
</script>

<template>
	<section
		v-if="!hasLoadedOnce || isInitialLoading"
		class="w-full bg-white"
		data-testid="property-recommended-shorts-feed-skeleton"
		aria-busy="true"
	>
		<Container
			class="py-8! max-md:px-4! md:py-[60px]!"
			data-testid="recommended-feed-container"
		>
			<header class="mb-6 flex flex-col gap-1 md:mb-9 md:gap-2">
				<Skeleton
					is-active
					variant="white"
					class="h-7 w-64 max-w-full rounded-none md:h-9 md:w-96"
				/>
				<Skeleton
					is-active
					variant="white"
					class="h-4 w-72 max-w-full rounded-none md:h-5 md:w-80"
				/>
			</header>

			<div
				class="grid grid-cols-2 gap-1 md:grid-cols-3 md:gap-3 lg:grid-cols-5"
			>
				<Skeleton
					v-for="skeletonIndex in SKELETON_COUNT"
					:key="`initial-recommendation-skeleton-${skeletonIndex}`"
					is-active
					variant="white"
					class="aspect-3/4 rounded-none"
					:class="{
						'hidden md:block': skeletonIndex === 3,
						'hidden lg:block': skeletonIndex > 3
					}"
					data-testid="recommended-card-skeleton"
				/>
			</div>
		</Container>
	</section>

	<section
		v-else-if="items.length"
		class="w-full bg-white"
		data-testid="property-recommended-shorts-feed"
		:aria-busy="isLoadingMore"
	>
		<Container
			class="py-8! max-md:px-4! md:py-[60px]!"
			data-testid="recommended-feed-container"
		>
			<header class="mb-6 flex flex-col gap-1 md:mb-9 md:gap-2">
				<h2
					class="font-matter-sq text-2xl font-normal leading-[150%] tracking-[-0.03rem] text-primary md:text-4xl md:tracking-[-0.045rem]"
				>
					{{ sectionTitle }}
				</h2>
				<p v-if="sectionSubtitle" class="text-sm text-primary md:text-base">
					{{ sectionSubtitle }}
				</p>
			</header>

			<div
				class="grid grid-cols-2 gap-1 md:grid-cols-3 md:gap-3 lg:grid-cols-5"
			>
				<div
					v-for="(item, index) in items"
					:key="item.property.commercialId"
					:ref="getCardRef(item.property.commercialId)"
					:data-recommended-card-commercial-id="item.property.commercialId"
				>
					<VideoCardHomeFeed
						:property="item.property"
						:thumbnail-url="item.thumbnailUrl"
						:video-url="item.videoUrl"
						:index="index"
						:is-active="
							isMobile &&
							activeMobileCardCommercialId === item.property.commercialId
						"
						:destination-url="`/shorts/${item.property.commercialId}`"
						layout="pdp-recommendations"
						section-name="pdp feed shorts"
						card-type="shorts card"
						:img-eager-count="4"
						:img-preload-count="2"
						player-root-margin="200px"
						@click="handleCardClick"
					/>
				</div>

				<template v-if="isLoadingMore">
					<Skeleton
						v-for="skeletonIndex in SKELETON_COUNT"
						:key="`pagination-recommendation-skeleton-${skeletonIndex}`"
						is-active
						variant="white"
						class="aspect-3/4 rounded-none"
						:class="{
							'hidden md:block': skeletonIndex === 3,
							'hidden lg:block': skeletonIndex > 3
						}"
						data-testid="recommended-card-skeleton"
					/>
				</template>
			</div>

			<div
				ref="loadMoreRef"
				class="h-16 w-full opacity-0 [overflow-anchor:none]"
				aria-hidden="true"
			/>
		</Container>
	</section>
</template>
